import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

interface SignalModalProps {
  isOpen: boolean;
  onClose: () => void;
  signals: string[];
  selectedSignals: string[];
  initialPairs: { signal1: string; signal2: string }[];
  onSave: (signalPairs: { signal1: string; signal2: string }[]) => void;
  setPairModified:any;
}

const SignalModal = ({
  isOpen,
  onClose,
  signals,
  initialPairs,
  onSave,
  setPairModified,
}: SignalModalProps) => {
  const [signalPairs, setSignalPairs] = useState(initialPairs);
  const [signalSelectionModifed, setSignalSelectionModified] = useState(false);
  useEffect(() => {
    setSignalPairs(initialPairs);
  }, [initialPairs]);

  const addSignalPair = () => {
    if (signalPairs.length < 6) {
      setSignalPairs([...signalPairs, { signal1: "", signal2: "" }]);
    }
  };

  const handleSignalChange = (
    index: number,
    key: "signal1" | "signal2",
    value: string
  ) => {
    if(index > -1) {
      setPairModified(index?.toString())
    }
    const newSignalPairs = [...signalPairs];
    newSignalPairs[index][key] = value;
    setSignalPairs(newSignalPairs);
    setSignalSelectionModified(true);
   
  };

  const handleSave = () => {
    const validSignalPairs = signalPairs.filter(pair => pair.signal1 && pair.signal2);
    onSave(validSignalPairs);
    setSignalSelectionModified(false);
};

  const isValidSelection = () => {
    const selectedSignalsCount = signalPairs.reduce((count, pair) => {
      return count + (pair.signal1 ? 1 : 0) + (pair.signal2 ? 1 : 0);
    }, 0);

    const hasNoDuplicatePairs = signalPairs.every((pair, index) => 
      signalPairs.findIndex(p => p.signal1 === pair.signal1 && p.signal2 === pair.signal2) === index
    );

    const hasNoSameSignalsInPairs = signalPairs.every(pair => 
      (pair.signal1 === "" && pair.signal2 === "") || (pair.signal1 !== pair.signal2)
    );

    const isEvenSelection = selectedSignalsCount % 2 === 0;

    return isEvenSelection && selectedSignalsCount >= 2 && hasNoDuplicatePairs && hasNoSameSignalsInPairs && signalSelectionModifed;
  };

  return (
    <Dialog open={isOpen} onClose={(event, reason) => {
      if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
        onClose();
        setSignalSelectionModified(false);
      }
    }}  fullWidth maxWidth="sm">
      <DialogTitle>Select Signal Pairs for Scatter Plot</DialogTitle>
      <DialogContent>
        {signalPairs.map((pair, index) => (
          <Grid
            container
            spacing={2}
            key={index}
            style={{ marginBottom: "10px" }}
          >
            <Grid item xs={6} style={{ paddingTop: "24px" }}>
              <FormControl fullWidth>
                <InputLabel>Signal {index * 2 + 1}</InputLabel>
                <Select
                  value={pair.signal1}
                  onChange={(e) =>
                    handleSignalChange(
                      index,
                      "signal1",
                      e.target.value as string
                    )
                  }
                  label={`Signal ${index * 2 + 1}`}
                >
                  {signals.map((signal) => (
                    <MenuItem key={signal} value={signal}>
                      {signal}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} style={{ paddingTop: "24px" }}>
              <FormControl fullWidth>
                <InputLabel>Signal {index * 2 + 2}</InputLabel>
                <Select
                  value={pair.signal2}
                  onChange={(e) =>
                    handleSignalChange(
                      index,
                      "signal2",
                      e.target.value as string
                    )
                  }
                  label={`Signal ${index * 2 + 2}`}
                >
                  {signals.map((signal) => (
                    <MenuItem key={signal} value={signal}>
                      {signal}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        ))}
        <IconButton
          color="primary"
          onClick={addSignalPair}
          aria-label="add pair"
          disabled={signalPairs.length >= 6} 
        >
          <AddCircleOutlineIcon />
        </IconButton>
        {!isValidSelection() && (
          <Typography color="error" style={{ marginTop: "10px" }}>
            Please select at least 2 different signals. Signals in a pair must
            be different.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant='contained' onClick={() => {onClose(); setSignalSelectionModified(false);}}>Cancel</Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant='contained'
          disabled={!isValidSelection()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SignalModal;
